import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, FileJson, FileSpreadsheet, Loader2, TriangleAlert } from "lucide-react";

const downloadBlob = (data, filename, type) => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const DataTab = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/org/export?format=${format}`, { responseType: "blob" });
      downloadBlob(res.data, format === "csv" ? "biipp-items-export.csv" : "biipp-export.json",
        format === "csv" ? "text/csv" : "application/json");
      toast.success("Export ready — your download has started.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setExporting(null);
    }
  };

  const deleteOrg = async () => {
    setDeleting(true);
    try {
      await api.post("/org/delete", { confirm_name: confirmName });
      toast.success("Your organization and all its data have been deleted.");
      setUser(null);
      navigate("/login");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Export your data</CardTitle>
          <CardDescription>
            Download everything your organization has stored in BIIPP. Encrypted reference numbers are included in decrypted form — handle the file with care.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3">
          <Button variant="secondary" className="rounded-xl flex-1" disabled={exporting === "json"}
            onClick={() => doExport("json")} data-testid="export-json-button">
            {exporting === "json" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileJson className="h-4 w-4 mr-1.5" /> Full export (JSON)</>}
          </Button>
          <Button variant="secondary" className="rounded-xl flex-1" disabled={exporting === "csv"}
            onClick={() => doExport("csv")} data-testid="export-csv-button">
            {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Items (CSV)</>}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-[hsl(var(--destructive)/0.3)]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-[hsl(var(--destructive))]">
            <TriangleAlert className="h-4 w-4" /> Danger zone
          </CardTitle>
          <CardDescription>
            Permanently delete {user?.org?.name} — every item, category, teammate, reminder and audit record. There is no undo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-xl border-[hsl(var(--destructive)/0.4)] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.06)] hover:text-[hsl(var(--destructive))]"
                data-testid="org-delete-open-button">
                Delete organization
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {user?.org?.name} forever?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes all data for every member of your organization, permanently. Consider exporting first. Type{" "}
                  <span className="font-semibold text-foreground">{user?.org?.name}</span> to confirm.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2">
                <Label htmlFor="confirm-org-name" className="sr-only">Organization name</Label>
                <Input id="confirm-org-name" value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={user?.org?.name} data-testid="org-delete-confirm-input" />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-xl" onClick={() => setConfirmName("")}>Keep my organization</AlertDialogCancel>
                <AlertDialogAction onClick={deleteOrg}
                  disabled={confirmName !== user?.org?.name || deleting}
                  className="rounded-xl bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
                  data-testid="org-delete-confirm-button">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete everything"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
};
